import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "@refinedev/core";
import * as Sentry from "@sentry/react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getlostFirebaseAuth } from "../../utils/auth/firebaseConfig";
import { apiVersion } from "../../utils/api";

type OnboardingStep = 'username' | 'name' | 'photo';

interface ProfileData {
  username: string;
  name: string;
  photo?: { url: string };
}

export const OnboardingPage: React.FC = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  
  const [step, setStep] = React.useState<OnboardingStep>('username');
  const [profileData, setProfileData] = React.useState<ProfileData>({
    username: '',
    name: '',
  });
  
  const [username, setUsername] = React.useState('');
  const [usernameError, setUsernameError] = React.useState<string | undefined>();
  const [usernameValidating, setUsernameValidating] = React.useState(false);
  const [usernameValid, setUsernameValid] = React.useState(false);
  
  const [name, setName] = React.useState('');
  const [nameError, setNameError] = React.useState<string | undefined>();
  
  const [photoUrl, setPhotoUrl] = React.useState<string | undefined>();
  const [photoUploading, setPhotoUploading] = React.useState(false);
  const [photoError, setPhotoError] = React.useState<string | undefined>();
  
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const usernameCheckTimeoutRef = React.useRef<NodeJS.Timeout>();

  // Debounced username validation
  React.useEffect(() => {
    if (!username) {
      setUsernameValid(false);
      setUsernameError(undefined);
      return;
    }

    // Clear previous timeout
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }

    // Trim and validate
    const trimmedUsername = username.trim();
    
    // Check for spaces
    if (trimmedUsername.includes(' ')) {
      setUsernameValid(false);
      setUsernameError(t('onboarding.username.spaces', 'Username cannot contain spaces'));
      return;
    }
    
    // Check minimum length
    if (trimmedUsername.length < 3) {
      setUsernameValid(false);
      setUsernameError(t('onboarding.username.short', 'Username must be at least 3 characters'));
      return;
    }
    
    // Check maximum length
    if (trimmedUsername.length > 50) {
      setUsernameValid(false);
      setUsernameError(t('onboarding.username.long', 'Username must be at most 50 characters'));
      return;
    }
    
    // Basic validation pattern
    const usernamePattern = /^[a-zA-Z0-9_]{3,50}$/;
    if (!usernamePattern.test(trimmedUsername)) {
      setUsernameValid(false);
      setUsernameError(t('onboarding.username.invalid', 'Username must be 3-50 characters (letters, numbers, underscore only)'));
      return;
    }

    setUsernameError(undefined);
    setUsernameValidating(true);

    // Debounce the API call
    usernameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const base = import.meta.env.VITE_API;
        // Per API spec, validate username via POST /admin/v1/profile?id=username
        const url = `${base}/admin/${apiVersion}/profile/${encodeURIComponent(username)}`;
        // Attach Firebase ID token for auth when cookies are not yet set
        const token = await getlostFirebaseAuth.auth.currentUser?.getIdToken?.();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(url, { method: 'GET', credentials: 'include', headers });
        
        if (response.status === 404) {
          setUsernameValid(true);
          setUsernameError(undefined);
        } else if (response.status === 201 || response.status === 200 || response.status === 204) {
          setUsernameValid(false);
          const errorData = await response.json().catch(() => ({}));
          setUsernameError(errorData?.message || t('onboarding.username.taken', 'Username is already taken or invalid'));
        } else {
          setUsernameValid(false);
          setUsernameError(t('onboarding.username.error', 'Failed to validate username. Please try again.'));
        }
      } catch (err: any) {
        setUsernameValid(false);
        setUsernameError(err?.message || t('onboarding.username.error', 'Failed to validate username. Please try again.'));
      } finally {
        setUsernameValidating(false);
      }
    }, 500);

    return () => {
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
    };
  }, [username, t]);

  const handleUsernameNext = () => {
    if (!usernameValid || usernameValidating) return;
    const trimmedUsername = username.trim();
    setProfileData(prev => ({ ...prev, username: trimmedUsername }));
    setStep('name');
  };

  const handleNameNext = () => {
    if (!name.trim()) {
      setNameError(t('onboarding.name.required', 'Name is required'));
      return;
    }
    setProfileData(prev => ({ ...prev, name: name.trim() }));
    setStep('photo');
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoError(undefined);
    setPhotoUploading(true);

    // Create local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      const previewUrl = e.target?.result as string;
      setPhotoUrl(previewUrl);
    };
    reader.readAsDataURL(file);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const base = import.meta.env.VITE_API;
      const url = `${base}/admin/${apiVersion}/image/thumb`;

      const token = await getlostFirebaseAuth.auth.currentUser?.getIdToken?.();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        throw new Error('Photo upload failed');
      }

      const data = await response.json();
      const firstKey = Object.keys(data)[0];
      
      setPhotoUrl(data[firstKey].url);
      setProfileData(prev => ({ ...prev, photo: { url: data[firstKey].url } }));
    } catch (err: any) {
      setPhotoError(err?.message || t('onboarding.photo.error', 'Failed to upload photo'));
      // Keep the preview visible even if upload fails
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setError(undefined);

    try {
      const base = import.meta.env.VITE_API;
      const url = `${base}/admin/${apiVersion}/profile`;

      const body: any = {
        id: profileData.username,
        name: profileData.name,
      };

      if (profileData.photo) {
        body.photo = profileData.photo;
      }

      const token = await getlostFirebaseAuth.auth.currentUser?.getIdToken?.();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to create profile');
      }

      // Profile created successfully
      console.log("Profile created successfully");

      // Re-establish admin session with the newly created profile
      try {
        await getlostFirebaseAuth.establishAdminSession();
        console.log("Admin session re-established with new profile");
      } catch (sessionErr: any) {
        console.error("Warning: Failed to re-establish admin session:", sessionErr);
        // Continue anyway - the session might establish on next request
      }

      // Navigate to home
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.message || t('onboarding.error', 'Failed to complete onboarding. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPhoto = () => {
    handleFinish();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          "radial-gradient(50% 50% at 50% 50%,rgba(255, 255, 255, 0) 0%,rgba(0, 0, 0, 0.5) 100%),url('images/background-getlost.png')",
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
        padding: 3,
      }}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(18, 18, 18, 0.9)',
          borderRadius: 2,
          padding: 4,
          maxWidth: 500,
          width: '100%',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} marginBottom={4}>
          <img src="/images/getlost-logo.png" alt="GetLost Logo" height={72} />
        </Box>

        <Typography variant="h5" color="white" textAlign="center" gutterBottom>
          {t('onboarding.title', 'Welcome to GetLost!')}
        </Typography>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" textAlign="center" mb={4}>
          {t('onboarding.subtitle', "Let's set up your profile")}
        </Typography>

        {step === 'username' && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" color="white" mb={1}>
                {t('onboarding.username.title', 'Choose your username')}
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" mb={2}>
                {t('onboarding.username.description', 'This will be your unique identifier')}
              </Typography>
              <TextField
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
                placeholder={t('onboarding.username.placeholder', 'username')}
                fullWidth
                autoFocus
                error={!!usernameError}
                helperText={usernameError || 'Letters, numbers, underscore only. 3-50 characters.'}
                InputProps={{
                  endAdornment: usernameValidating ? (
                    <CircularProgress size={20} />
                  ) : usernameValid ? (
                    <CheckCircleIcon color="success" />
                  ) : null,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    color: '#fff',
                  },
                  '& .MuiFormHelperText-root': {
                    color: usernameError ? 'error.main' : 'rgba(255, 255, 255, 0.6)',
                  },
                }}
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleUsernameNext}
              disabled={!usernameValid || usernameValidating}
              fullWidth
              size="large"
            >
              {t('onboarding.next', 'Next')}
            </Button>
          </Stack>
        )}

        {step === 'name' && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" color="white" mb={1}>
                {t('onboarding.name.title', "What's your name?")}
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" mb={2}>
                {t('onboarding.name.description', 'This is how others will see you')}
              </Typography>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('onboarding.name.placeholder', 'Your name')}
                fullWidth
                autoFocus
                error={!!nameError}
                helperText={nameError}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    color: '#fff',
                  },
                  '& .MuiFormHelperText-root': {
                    color: nameError ? 'error.main' : 'rgba(255, 255, 255, 0.6)',
                  },
                }}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setStep('username')}
                fullWidth
                size="large"
              >
                {t('onboarding.back', 'Back')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNameNext}
                disabled={!name.trim()}
                fullWidth
                size="large"
              >
                {t('onboarding.next', 'Next')}
              </Button>
            </Stack>
          </Stack>
        )}

        {step === 'photo' && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" color="white" mb={1}>
                {t('onboarding.photo.title', 'Add a profile photo')}
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" mb={3}>
                {t('onboarding.photo.description', 'Optional - you can add this later')}
              </Typography>

              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box position="relative">
                  <Avatar
                    src={photoUrl}
                    sx={{
                      width: 120,
                      height: 120,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      fontSize: '3rem',
                    }}
                  >
                    {!photoUrl && name.charAt(0).toUpperCase()}
                  </Avatar>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                  </IconButton>
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />

                {photoError && <Alert severity="error" sx={{ width: '100%' }}>{photoError}</Alert>}
              </Box>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setStep('name')}
                fullWidth
                size="large"
                disabled={loading}
              >
                {t('onboarding.back', 'Back')}
              </Button>
              {!photoUrl ? (
                <Button
                  variant="outlined"
                  onClick={handleSkipPhoto}
                  fullWidth
                  size="large"
                  disabled={loading || photoUploading}
                >
                  {loading || photoUploading ? <CircularProgress size={24} /> : t('onboarding.skip', 'Skip')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFinish}
                  fullWidth
                  size="large"
                  disabled={loading || photoUploading}
                >
                  {loading || photoUploading ? <CircularProgress size={24} /> : t('onboarding.finish', 'Finish')}
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};
